/**
 * Cognito auth via Amplify v6 (Slice AUTH, 2026-05-29).
 *
 * Configures the user pool from VITE_USER_POOL_ID / VITE_USER_POOL_CLIENT_ID
 * and exposes useAuth() for the login gate. Tokens for API calls are read
 * separately in core/api/token.ts (fetchAuthSession), so non-React modules
 * don't depend on this provider.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  fetchUserAttributes,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

/**
 * Invitation-only access list. **UX only** — the real access boundary is
 * Cognito (admin-created users, self-signup disabled via AllowAdminCreateUserOnly).
 * This list just decides whether the "Forgot password?" link appears. Add an
 * email here when you invite a user.
 */
export const APPROVED_EMAILS = ["gabor@sigongroup.com", "tyler@sigongroup.com"];
export const isApprovedEmail = (email: string): boolean =>
  APPROVED_EMAILS.includes(email.trim().toLowerCase());

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID ?? "";
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID ?? "";

if (USER_POOL_ID && USER_POOL_CLIENT_ID) {
  Amplify.configure({
    Auth: { Cognito: { userPoolId: USER_POOL_ID, userPoolClientId: USER_POOL_CLIENT_ID } },
  });
}

interface AuthUser {
  email: string;
}

interface AuthState {
  user: AuthUser | null;
  ready: boolean; // initial session check finished
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Email a reset code (Cognito forgot-password). Gated to approved emails in the UI. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Complete the reset with the emailed code + a new password. */
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  const loadUser = useCallback(async () => {
    try {
      const attrs = await fetchUserAttributes();
      setUser({ email: attrs.email ?? "" });
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void loadUser().finally(() => setReady(true));
  }, [loadUser]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await amplifySignIn({ username: email, password });
      await loadUser();
    },
    [loadUser]
  );

  const signOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await resetPassword({ username: email.trim() });
  }, []);

  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.trim(),
        newPassword,
      });
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{ user, ready, signIn, signOut, requestPasswordReset, confirmPasswordReset }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
